import React, { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import OriginTracer from '@atlassiansox/origin-tracing';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';
import { CoreInvitesSource } from '@confluence/core-invites-provider/entry-points/constants.ts';
import type {
	FlagsStateContainer,
	FlagDescriptor,
} from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useCopyToClipboard } from '@confluence/copy-content-link/entry-points/useCopyToClipboard.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';

const messages = defineMessages({
	inviteFlagTitle: {
		id: 'contextual-invite-nudge-flag.title',
		defaultMessage: 'You’ve created your first page 🎉',
		description:
			'The title used in a flag to nudge the user to invite other users into confluence.',
	},
	inviteFlagTitleLiveDoc: {
		id: 'contextual-invite-nudge-flag.title.live-doc',
		defaultMessage: 'You’ve created your first live doc 🎉',
		description:
			'The title used in a flag to nudge the user to invite other users into confluence after creating a live doc.',
	},
	inviteFlagDescription: {
		id: 'contextual-invite-nudge-flag.description',
		defaultMessage: 'Add your teammates so they can collaborate with you.',
		description:
			'The description used in a flag to nudge the user to invite other users into confluence.',
	},
	inviteFlagAdminCTA: {
		id: 'contextual-invite-nudge-flag.adminCTA',
		defaultMessage: 'Copy shareable invite link',
		description:
			'This is the text on a button for admin users to copy an invite link that they can share with other users to invite them to Confluence.',
	},
	inviteFlagAdminCopiedCTA: {
		id: 'contextual-invite-nudge-flag.adminCTA.copied',
		defaultMessage: 'Copied!',
		description:
			'This is the text shown to confirm that the invite link has been successfully copied to the clipboard for admin users.',
	},
	inviteFlagNthUserCTA: {
		id: 'contextual-invite-nudge-flag.nthUserCTA',
		defaultMessage: 'Invite people',
		description:
			'The action of the flag to nudge the nth user to open the invite modal to invite other users.',
	},
});

const ADMIN_FLAG_ID = 'contextual-invite-nudge-admin-user-flag';
const END_USER_FLAG_ID = 'contextual-invite-nudge-end-user-flag';

// Constants for source values
export const LIVE_PAGE_SOURCE = 'live-page' as const;

/**
 * Source for analytics tracking. 'live-page' shows special copy for live docs.
 */
export type ContextualInviteNudgeSource = 'live-page' | string;

// Determines if live doc title should be used based on source
const shouldUseLiveDocTitle = (source: ContextualInviteNudgeSource): boolean => {
	return source === LIVE_PAGE_SOURCE;
};

export interface ContextualInviteNudgeFlagBaseProps {
	flags: FlagsStateContainer;
	source: ContextualInviteNudgeSource;
}

type ContextualInviteNudgeFlagProps = {
	flags: FlagsStateContainer;
	source: ContextualInviteNudgeSource;
};

export const ContextualInviteNudgeFlag = withTransparentErrorBoundary({
	attribution: Attribution.FLYWHEEL_ACTIVATION,
})(
	withFlags(({ flags, source }: ContextualInviteNudgeFlagBaseProps) => {
		const {
			data: onboardingStateData,
			loading: onboardingStateLoading,
			error: onboardingStateError,
		} = useGetOnboardingState([ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN]);

		const { isFoundingAdmin } = deserializeState(onboardingStateData);

		const isAdmin = isFoundingAdmin;

		if (onboardingStateLoading || onboardingStateError) {
			return null;
		}

		if (isAdmin) {
			return (
				<>
					<AdminFlag flags={flags} source={source} />
				</>
			);
		}

		return (
			<>
				<EndUserFlag flags={flags} source={source} />
			</>
		);
	}),
);

const TIMEOUT_INTERVAL = 2000;

const AdminFlag = ({ flags, source }: ContextualInviteNudgeFlagProps) => {
	const [isInviteLinkCopied, setIsInviteLinkCopied] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>();
	const hasShownFlag = useRef(false);

	const { copyToClipboard } = useCopyToClipboard();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { inviteLink } = useMemo(() => {
		const rawInviteLink = window.location.href;
		const origin = new OriginTracer({ product: 'confluence' });
		return {
			inviteLink: origin.addToUrl(rawInviteLink),
		};
	}, []);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'adminContextualInviteNudgeFlag',
				source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	useEffect(() => {
		if (isInviteLinkCopied) {
			timer.current = setTimeout(() => {
				setIsInviteLinkCopied(false);
			}, TIMEOUT_INTERVAL);
		} else {
			clearInterval(timer.current);
		}

		return () => clearInterval(timer.current);
	}, [isInviteLinkCopied, setIsInviteLinkCopied, timer]);

	const onCopy = useCallback(() => {
		void copyToClipboard(inviteLink).then(() => {
			setIsInviteLinkCopied(true);
		});

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'adminContextualInviteNudgeFlagCopyClick',
				source,
			},
		}).fire();
	}, [copyToClipboard, inviteLink, createAnalyticsEvent, source]);

	const flagDescriptor: FlagDescriptor = useMemo(() => {
		return {
			id: ADMIN_FLAG_ID,
			type: 'success-circle',
			messageId: ADMIN_FLAG_ID,
			messageType: 'engagement',
			title: (
				<FormattedMessage
					{...(shouldUseLiveDocTitle(source)
						? messages.inviteFlagTitleLiveDoc
						: messages.inviteFlagTitle)}
				/>
			),
			description: <FormattedMessage {...messages.inviteFlagDescription} />,
			actions: [
				isInviteLinkCopied
					? {
							content: <FormattedMessage {...messages.inviteFlagAdminCopiedCTA} />,
						}
					: {
							content: <FormattedMessage {...messages.inviteFlagAdminCTA} />,
							onClick: onCopy,
						},
			],
			close: 'never',
			onClose: () => {
				void flags.hideFlag(ADMIN_FLAG_ID);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'adminContextualInviteNudgeFlagDismissClick',
						source,
					},
				}).fire();
			},
		};
	}, [isInviteLinkCopied, onCopy, flags, source, createAnalyticsEvent]);

	useEffect(() => {
		if (inviteLink) {
			if (!hasShownFlag.current) {
				void flags.showFlag(flagDescriptor);
				hasShownFlag.current = true;
			} else {
				void flags.updateFlag(ADMIN_FLAG_ID, {
					actions: flagDescriptor.actions,
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inviteLink, isInviteLinkCopied]);

	return null;
};

const EndUserFlag = ({ flags, source }: ContextualInviteNudgeFlagProps) => {
	const { openCoreInvites } = useContext(CoreInvitesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'endUserContextualInviteNudgeFlag',
				source,
			},
		}).fire();
	}, [createAnalyticsEvent, source]);

	const flagDescriptor: FlagDescriptor = useMemo(() => {
		return {
			id: END_USER_FLAG_ID,
			type: 'success-circle',
			messageId: END_USER_FLAG_ID,
			messageType: 'engagement',
			title: (
				<FormattedMessage
					{...(shouldUseLiveDocTitle(source)
						? messages.inviteFlagTitleLiveDoc
						: messages.inviteFlagTitle)}
				/>
			),
			description: <FormattedMessage {...messages.inviteFlagDescription} />,
			actions: [
				{
					content: <FormattedMessage {...messages.inviteFlagNthUserCTA} />,
					onClick: () => {
						openCoreInvites?.(CoreInvitesSource.CONTEXTUAL_INVITE_NUDGE);
						void flags.hideFlag(END_USER_FLAG_ID);
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'endUserContextualInviteNudgeFlagInviteClick',
								source,
							},
						}).fire();
					},
				},
			],
			close: 'never',
			onClose: () => {
				void flags.hideFlag(END_USER_FLAG_ID);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId: 'endUserContextualInviteNudgeFlagDismissClick',
						source,
					},
				}).fire();
			},
		};
	}, [flags, openCoreInvites, source, createAnalyticsEvent]);

	useEffect(() => {
		void flags.showFlag(flagDescriptor);
	}, [flagDescriptor, flags]);

	return null;
};
